import React from "react";
import styled from "styled-components";
import { Card, Col, Empty, Row, Skeleton, Space, Statistic, Tag, Typography } from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
  FileDoneOutlined,
  PercentageOutlined,
  RiseOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const StripRow = styled(Row)`
  margin-bottom: 24px;
`;

const KpiCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .ant-card-body {
    padding: 20px;
  }
`;

const ComparisonRow = styled(Space)`
  margin-top: 10px;
  min-height: 22px;
`;

const EmptyCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
`;

const KPI_DEFINITIONS = [
  {
    key: "total_leads",
    sourceGroup: "executive",
    title: "Total Leads",
    icon: <UserOutlined />,
    color: "#1890ff",
    priority: "high",
  },
  {
    key: "qualified_leads",
    sourceGroup: "sales",
    title: "Qualified Leads",
    icon: <TeamOutlined />,
    color: "#722ed1",
    priority: "high",
  },
  {
    key: "quotes_sent",
    sourceGroup: "executive",
    title: "Quotes Sent",
    icon: <FileDoneOutlined />,
    color: "#faad14",
    priority: "high",
  },
  {
    key: "bookings_confirmed",
    sourceGroup: "executive",
    title: "Bookings Confirmed",
    icon: <CheckCircleOutlined />,
    color: "#005249",
    priority: "high",
  },
  {
    key: "revenue_booked",
    sourceGroup: "finance",
    title: "Revenue Booked",
    icon: <RiseOutlined />,
    color: "#13c2c2",
    priority: "high",
  },
  {
    key: "revenue_collected",
    sourceGroup: "finance",
    title: "Revenue Collected",
    icon: <DollarCircleOutlined />,
    color: "#52c41a",
    priority: "high",
  },
  {
    key: "lead_to_order_conversion_rate",
    sourceGroup: "executive",
    title: "Conversion Rate",
    icon: <PercentageOutlined />,
    color: "#eb2f96",
    priority: "normal",
  },
  {
    key: "active_trips",
    sourceGroup: "operations",
    title: "Active Trips",
    icon: <CalendarOutlined />,
    color: "#fa541c",
    priority: "normal",
  },
];

const ExecutiveKpiStrip = ({ metricGroups, loading = false }) => {
  if (loading) {
    return (
      <StripRow gutter={[24, 24]} data-testid="executive-kpi-strip-loading">
        {KPI_DEFINITIONS.map((definition) => (
          <Col xs={24} sm={12} xl={6} key={definition.key}>
            <KpiCard>
              <Skeleton active paragraph={{ rows: 2 }} />
            </KpiCard>
          </Col>
        ))}
      </StripRow>
    );
  }

  if (!hasAvailableMetricGroups(metricGroups)) {
    return (
      <EmptyCard>
        <Empty
          description="Executive KPI metrics are not available yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </EmptyCard>
    );
  }

  return (
    <StripRow gutter={[24, 24]} data-testid="executive-kpi-strip">
      {KPI_DEFINITIONS.map((definition) => {
        const metric = findMetric(metricGroups, definition);
        return (
          <Col xs={24} sm={12} xl={6} key={definition.key}>
            <ExecutiveKpiCard definition={definition} metric={metric} />
          </Col>
        );
      })}
    </StripRow>
  );
};

const ExecutiveKpiCard = ({ definition, metric }) => {
  const drilldownKey = metric?.drilldownKey || definition.key;
  const trend = getTrendDisplay(metric);

  return (
    <KpiCard data-drilldown-key={drilldownKey} aria-label={`${definition.title} KPI`}>
      <Statistic
        title={definition.title}
        value={metric ? formatMetricValue(metric) : "-"}
        prefix={definition.icon}
        valueStyle={{
          color: definition.color,
          fontSize: definition.priority === "high" ? "30px" : "26px",
          fontWeight: definition.priority === "high" ? 700 : 600,
        }}
      />
      <ComparisonRow size={6} wrap>
        <Tag color={trend.color}>{trend.label}</Tag>
        <Text type="secondary">{trend.context}</Text>
      </ComparisonRow>
    </KpiCard>
  );
};

function hasAvailableMetricGroups(metricGroups) {
  return Object.values(metricGroups || {}).some(
    (group) => Array.isArray(group?.metrics) && group.metrics.length > 0
  );
}

function findMetric(metricGroups, definition) {
  const preferredMetric = metricGroups?.[definition.sourceGroup]?.metrics?.find(
    (metric) => metric.key === definition.key
  );

  if (preferredMetric) return preferredMetric;

  return Object.values(metricGroups || {})
    .flatMap((group) => group?.metrics || [])
    .find((metric) => metric.key === definition.key);
}

function formatMetricValue(metric) {
  const value = Number(metric?.value || 0);

  if (metric.unit === "currency_zar") {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  if (metric.unit === "percent") {
    return `${value}%`;
  }

  return new Intl.NumberFormat("en-ZA").format(value);
}

function getTrendDisplay(metric) {
  if (!metric) {
    return {
      color: "default",
      label: "Unavailable",
      context: "Metric source pending",
    };
  }

  if (metric.changePercent === null || metric.changePercent === undefined) {
    return {
      color: "default",
      label: formatPreviousValue(metric),
      context: metric.comparisonLabel || "No previous comparison",
    };
  }

  if (!metric.changeAbsolute) {
    return {
      color: "default",
      label: "0%",
      context: metric.comparisonLabel || "vs previous period",
    };
  }

  const isPositive = metric.trendDirection === "up";
  const signedChange = `${isPositive ? "+" : "-"}${Math.abs(metric.changePercent)}%`;

  return {
    color: isPositive ? "green" : "red",
    label: signedChange,
    context: metric.comparisonLabel || "vs previous period",
  };
}

function formatPreviousValue(metric) {
  if (metric.previousValue === null || metric.previousValue === undefined) {
    return "No baseline";
  }

  return `Prev ${formatMetricValue({
    value: metric.previousValue,
    unit: metric.unit,
  })}`;
}

export default ExecutiveKpiStrip;
